import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent } from "./ui/card";
import { toast } from "sonner";
import {
  Bot, Send, Mic, MicOff, Loader2, Sparkles, CheckCircle2, MapPin, Clock, AlertCircle, X, Globe
} from "lucide-react";

// Check if Web Speech API is supported
const isSpeechSupported = () => {
  return typeof window !== 'undefined' && 
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);
};

export default function AIBookingAssistant({ onClose, initialQuery = "" }) {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hi! 👋 I'm your BQL Concierge. Tell me what service you need and where in New Jersey — in any language!\n\nFor example: \"I need a plumber in Newark\" or \"Necesito limpieza en Jersey City\""
    }
  ]);
  const [input, setInput] = useState(initialQuery);
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [readyToPost, setReadyToPost] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [creatingJob, setCreatingJob] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Initialize speech recognition with multi-language support
  useEffect(() => {
    if (isSpeechSupported()) {
      setSpeechSupported(true);
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = '';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        const isFinal = event.results[0].isFinal;
        
        if (isFinal) {
          setInput(prev => (prev ? prev + " " : "") + transcript);
          setIsListening(false);
          if (transcript.trim()) {
            setTimeout(() => {
              handleSend((input ? input + " " : "") + transcript);
            }, 500);
          }
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.log("Speech recognition error:", event.error);
        if (event.error === 'not-allowed') {
          toast.error("Please allow microphone access to use voice input");
        }
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, [input]);

  // Send initial query if provided
  useEffect(() => {
    if (initialQuery && messages.length === 1) {
      handleSend(initialQuery);
    }
  }, []);

  const toggleListening = () => {
    if (!recognitionRef.current) {
      toast.error("Voice input not supported in this browser. Please type your request.");
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      const userLang = navigator.language || 'en-US';
      recognitionRef.current.lang = userLang;
      
      try {
        recognitionRef.current.start();
        setIsListening(true);
        toast.info("Listening... Speak now 🎤", { duration: 2000 });
      } catch (e) {
        toast.error("Could not start voice input. Please try again.");
      }
    }
  };

  const handleSend = async (messageOverride) => {
    const messageText = messageOverride || input.trim();
    if (!messageText || loading) return;

    const userMessage = { role: "user", content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(
        `${API}/ai/chat`,
        {
          message: messageText,
          conversation_id: conversationId,
          context: extractedData || {}
        },
        { headers }
      );

      const { reply, conversation_id, extracted_data, ready_to_post } = response.data;
      
      setConversationId(conversation_id);
      setMessages(prev => [...prev, { role: "assistant", content: reply }]);
      
      if (extracted_data) {
        setExtractedData(extracted_data);
      }
      
      if (ready_to_post) {
        setReadyToPost(true);
      }
    } catch (error) {
      console.error("AI Chat error:", error);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Sorry, I'm having trouble connecting right now. Please try again or use the regular job posting form." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateJob = async () => {
    if (!isAuthenticated) {
      toast.info("Please log in or sign up to post your job");
      navigate("/login", { state: { returnTo: "/", aiData: extractedData } });
      return;
    }

    if (user?.user_type !== "customer") {
      toast.error("Only customers can post jobs. Please sign up as a customer.");
      return;
    }

    setCreatingJob(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/ai/create-job`,
        {
          service_category: extractedData.service_category,
          city: extractedData.city,
          urgency: extractedData.urgency || "standard",
          preferred_time: extractedData.preferred_time,
          description: extractedData.description,
          conversation_id: conversationId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Job posted successfully! We're finding pros for you.");
      setMessages(prev => [...prev, {
        role: "assistant",
        content: `🎉 Your job has been posted!\n\n**${response.data.job.title}**\n\nWe're now matching you with qualified professionals in ${response.data.job.location}. You'll receive quotes soon!\n\nCheck your dashboard to track responses.`
      }]);
      setReadyToPost(false);
      setExtractedData(null);

      setTimeout(() => {
        navigate("/dashboard");
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create job");
    } finally {
      setCreatingJob(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Card className="w-full max-w-lg mx-auto shadow-2xl border-0 overflow-hidden rounded-xl" data-testid="ai-booking-assistant">
      {/* Header - BQL Concierge */}
      <div className="bg-gradient-to-r from-navy to-navy-light p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
              <Bot className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-bold text-lg">BQL Concierge</h3>
              <div className="flex items-center gap-1 text-sm text-white/70">
                <Globe className="w-3 h-3" />
                <span>Any language supported</span>
              </div>
            </div>
          </div>
          {onClose && (
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Messages Area */}
      <CardContent className="p-0">
        <div className="h-80 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-slate-50 to-white">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] rounded-xl px-4 py-3 ${
                  msg.role === "user"
                    ? "bg-navy text-white rounded-br-sm"
                    : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm shadow-sm"
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          
          {/* Typing Indicator */}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white border border-slate-200 rounded-xl rounded-bl-sm px-4 py-3 shadow-sm">
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-navy rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-2 h-2 bg-navy rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-2 h-2 bg-navy rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Job Summary Card (when ready to post) */}
        {readyToPost && extractedData && (
          <div className="p-4 bg-emerald-50 border-t border-emerald-200">
            <div className="flex items-start gap-3 mb-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-emerald-900 text-sm">Ready to post your job!</p>
                <div className="mt-2 space-y-1 text-sm text-emerald-800">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    <span>{extractedData.service_category}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    <span>{extractedData.city}, NJ</span>
                  </div>
                  {extractedData.urgency && (
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      <span className="capitalize">{extractedData.urgency}</span>
                    </div>
                  )}
                  {extractedData.preferred_time && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{extractedData.preferred_time}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <Button
              onClick={handleCreateJob}
              disabled={creatingJob}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold"
              data-testid="ai-post-job-btn"
            >
              {creatingJob ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Creating Job...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Post Job Now
                </>
              )}
            </Button>
          </div>
        )}

        {/* Input Area */}
        <div className="p-4 border-t border-slate-100 bg-white">
          <div className="flex items-center gap-2">
            {/* ⚠️ LOCKED: Do NOT move, remove, or conditionally hide the mic button during UI updates */}
            {/* Microphone Button - PERMANENTLY FIXED on LEFT side of input */}
            {speechSupported && (
              <button
                onClick={toggleListening}
                disabled={loading}
                className={`flex-shrink-0 w-11 h-11 rounded-lg flex items-center justify-center transition-all ${
                  isListening 
                    ? "bg-red-500 text-white animate-pulse shadow-lg" 
                    : "bg-navy/10 text-navy hover:bg-navy/20"
                }`}
                type="button"
                data-testid="ai-voice-btn"
                title={isListening ? "Stop listening" : "Speak your request"}
                aria-label={isListening ? "Stop voice input" : "Start voice input"}
              >
                {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>
            )}
            {/* ⚠️ END LOCKED MIC BUTTON */}
            
            {/* Text Input */}
            <div className="flex-1 min-w-0">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={speechSupported ? "Type or tap mic to speak..." : "Describe what you need..."}
                className="rounded-lg border-slate-200 focus:border-navy focus:ring-navy w-full"
                disabled={loading}
                data-testid="ai-chat-input"
              />
            </div>
            
            {/* Send Button */}
            <Button
              onClick={() => handleSend()}
              disabled={!input.trim() || loading}
              className="flex-shrink-0 w-11 h-11 rounded-lg bg-navy hover:bg-navy-dark p-0 flex items-center justify-center"
              data-testid="ai-send-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Listening indicator */}
          {isListening && (
            <div className="mt-2 text-center">
              <span className="text-sm text-red-600 animate-pulse flex items-center justify-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-ping"></span>
                Listening... Speak now
              </span>
            </div>
          )}
          
          <p className="text-xs text-slate-500 text-center mt-2">
            Supports 50+ languages • 88 NJ service categories
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
